import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { NutritionPlan, MacroTargets, Meal } from '../../../types/program';

interface NutritionPlanTabProps {
  nutritionPlan: NutritionPlan | null;
  onUpdate: (plan: NutritionPlan) => void;
}

const NutritionPlanTab: React.FC<NutritionPlanTabProps> = ({ nutritionPlan, onUpdate }) => {
  const updateMacroTargets = (targets: MacroTargets) => {
    onUpdate({
      ...nutritionPlan!,
      macroTargets: targets,
    });
  };

  const addMeal = () => {
    const newMeal: Meal = {
      title: 'New Meal',
      description: '',
      nutrition: { calories: 0, protein: 0, carbs: 0, fats: 0 },
      order: nutritionPlan?.meals.length || 0,
    };

    onUpdate({
      ...nutritionPlan!,
      meals: [...(nutritionPlan?.meals || []), newMeal],
    });
  };

  const updateMeal = (index: number, updatedMeal: Meal) => {
    const updatedMeals = (nutritionPlan?.meals || []).map((meal, i) =>
      i === index ? updatedMeal : meal
    );

    onUpdate({
      ...nutritionPlan!,
      meals: updatedMeals,
    });
  };

  const deleteMeal = (index: number) => {
    const updatedMeals = (nutritionPlan?.meals || []).filter((_, i) => i !== index);

    onUpdate({
      ...nutritionPlan!,
      meals: updatedMeals,
    });
  };

  return (
    <div className="space-y-6">
      {/* Macro Targets */}
      <div className="bg-[#141414] rounded-xl border border-[#1F1F1F] p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Macro Targets</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Calories</label>
            <input
              type="number"
              value={nutritionPlan?.macroTargets.calories || ''}
              onChange={(e) =>
                updateMacroTargets({
                  ...nutritionPlan!.macroTargets,
                  calories: parseInt(e.target.value) || 0,
                })
              }
              className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Protein (g)</label>
            <input
              type="number"
              value={nutritionPlan?.macroTargets.protein || ''}
              onChange={(e) =>
                updateMacroTargets({
                  ...nutritionPlan!.macroTargets,
                  protein: parseInt(e.target.value) || 0,
                })
              }
              className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Carbs (g)</label>
            <input
              type="number"
              value={nutritionPlan?.macroTargets.carbs || ''}
              onChange={(e) =>
                updateMacroTargets({
                  ...nutritionPlan!.macroTargets,
                  carbs: parseInt(e.target.value) || 0,
                })
              }
              className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Fats (g)</label>
            <input
              type="number"
              value={nutritionPlan?.macroTargets.fats || ''}
              onChange={(e) =>
                updateMacroTargets({
                  ...nutritionPlan!.macroTargets,
                  fats: parseInt(e.target.value) || 0,
                })
              }
              className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Meal Plans */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Meal Plans</h3>
          <button
            onClick={addMeal}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#23F47C] text-[#0A0A0A] text-sm font-semibold rounded-lg hover:bg-[#1FD66D] transition-all"
          >
            <Plus size={16} />
            <span>Add Meal</span>
          </button>
        </div>

        {(nutritionPlan?.meals || []).map((meal, index) => (
          <div key={index} className="bg-[#141414] rounded-xl border border-[#1F1F1F] p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-[#23F47C]">Meal {index + 1}</span>
              <button
                onClick={() => deleteMeal(index)}
                className="text-[#FF4757] hover:text-[#FF6B7A] p-2 hover:bg-[rgba(255,71,87,0.1)] rounded-lg transition-all"
              >
                <Trash2 size={16} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Meal Title</label>
                <input
                  type="text"
                  value={meal.title}
                  onChange={(e) =>
                    updateMeal(index, { ...meal, title: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Description</label>
                <textarea
                  value={meal.description || ''}
                  onChange={(e) =>
                    updateMeal(index, { ...meal, description: e.target.value })
                  }
                  rows={2}
                  className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Calories</label>
                  <input
                    type="number"
                    value={meal.nutrition.calories}
                    onChange={(e) =>
                      updateMeal(index, {
                        ...meal,
                        nutrition: {
                          ...meal.nutrition,
                          calories: parseInt(e.target.value) || 0,
                        },
                      })
                    }
                    className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Protein (g)</label>
                  <input
                    type="number"
                    value={meal.nutrition.protein}
                    onChange={(e) =>
                      updateMeal(index, {
                        ...meal,
                        nutrition: {
                          ...meal.nutrition,
                          protein: parseInt(e.target.value) || 0,
                        },
                      })
                    }
                    className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Scheduled Time (e.g., 08:00 AM)</label>
                <input
                  type="text"
                  value={meal.scheduledTime || ''}
                  onChange={(e) =>
                    updateMeal(index, { ...meal, scheduledTime: e.target.value })
                  }
                  placeholder="08:00 AM"
                  className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
                />
              </div>
            </div>
          </div>
        ))}

        {(nutritionPlan?.meals || []).length === 0 && (
          <div className="text-center py-8 text-[#8F8F8F]">
            No meal plans yet. Add your first meal plan above.
          </div>
        )}
      </div>
    </div>
  );
};

export default NutritionPlanTab;
